// Colour maths for operator branding. Pure functions, no DOM, so they are unit-tested directly.
// Contrast follows WCAG 2.x: relative luminance of sRGB, ratio (L1 + 0.05) / (L2 + 0.05).

export type Rgb = { r: number; g: number; b: number };

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** "#4F46E5" / "4f46e5" / "#abc" → "#4f46e5". Null for anything else. */
export function normalizeHex(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const m = HEX.exec(value.trim());
  if (!m) return null;
  let hex = m[1]!.toLowerCase();
  if (hex.length === 3) hex = hex.replace(/./g, (c) => c + c);
  return `#${hex}`;
}

export function hexToRgb(hex: string): Rgb {
  const n = normalizeHex(hex);
  if (!n) throw new Error(`Not a hex colour: ${hex}`);
  const v = parseInt(n.slice(1), 16);
  return { r: (v >> 16) & 255, g: (v >> 8) & 255, b: v & 255 };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const h = (x: number) => Math.round(Math.min(255, Math.max(0, x))).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex: string): number {
  const { r, g, b } = hexToRgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/** Linear mix in sRGB: t = 0 → a, t = 1 → b. */
export function mix(a: string, b: string, t: number): string {
  const x = hexToRgb(a);
  const y = hexToRgb(b);
  return rgbToHex({ r: x.r + (y.r - x.r) * t, g: x.g + (y.g - x.g) * t, b: x.b + (y.b - x.b) * t });
}

export function rgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const BLACK = "#000000";
export const WHITE = "#ffffff";

/**
 * The same hue, shaded darker or lighter just enough to reach `min` contrast against every colour in
 * `backgrounds`. Direction: away from the backgrounds (darker on light grounds, lighter on dark
 * ones). Returns the input unchanged when it already passes.
 */
export function ensureContrast(color: string, backgrounds: string[], min: number): string {
  const base = normalizeHex(color);
  if (!base) throw new Error(`Not a hex colour: ${color}`);
  const worst = (c: string) => Math.min(...backgrounds.map((bg) => contrastRatio(c, bg)));
  if (worst(base) >= min) return base;
  const avgBgLum = backgrounds.reduce((s, bg) => s + relativeLuminance(bg), 0) / backgrounds.length;
  const target = avgBgLum > 0.18 ? BLACK : WHITE;
  for (let i = 1; i <= 40; i++) {
    const candidate = mix(base, target, i / 40);
    if (worst(candidate) >= min) return candidate;
  }
  return target;
}

export interface FilledColor {
  /** Background of the filled control (button). */
  bg: string;
  /** Text/icon colour on it. */
  fg: string;
  hover: string;
  press: string;
}

/** Near-black used for text on light brand colours (matches the checkout's own text colour). */
export const INK = "#0d1220";

/**
 * A filled button in the brand colour with readable text. Picks white or ink, whichever contrasts
 * more; if neither reaches `min` the background is shaded until the chosen text colour does.
 * Hover and press states move away from the text colour, so they only ever gain contrast.
 */
export function filledColor(color: string, min = 4.5): FilledColor {
  const base = normalizeHex(color);
  if (!base) throw new Error(`Not a hex colour: ${color}`);
  const onWhite = contrastRatio(WHITE, base);
  const onInk = contrastRatio(INK, base);
  const fg = onWhite >= onInk ? WHITE : INK;
  let bg = base;
  if (contrastRatio(fg, bg) < min) {
    const toward = fg === WHITE ? BLACK : WHITE;
    for (let i = 1; i <= 40; i++) {
      bg = mix(base, toward, i / 40);
      if (contrastRatio(fg, bg) >= min) break;
    }
  }
  const away = fg === WHITE ? BLACK : WHITE;
  return { bg, fg, hover: mix(bg, away, 0.08), press: mix(bg, away, 0.16) };
}

/** Rotate the hue by `degrees` in HSL, keeping saturation and lightness (used for gradient stops). */
export function hueShift(hex: string, degrees: number): string {
  const { r, g, b } = hexToRgb(hex);
  const [R, G, B] = [r / 255, g / 255, b / 255];
  const max = Math.max(R, G, B);
  const min = Math.min(R, G, B);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return rgbToHex({ r, g, b });
  const s = d / (1 - Math.abs(2 * l - 1));
  let h = max === R ? ((G - B) / d) % 6 : max === G ? (B - R) / d + 2 : (R - G) / d + 4;
  h = (((h * 60 + degrees) % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r1, g1, b1] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return rgbToHex({ r: (r1 + m) * 255, g: (g1 + m) * 255, b: (b1 + m) * 255 });
}

/** Shade `color` toward black or white until `fg` on it reaches `min` contrast. */
export function shadeFor(color: string, fg: string, min = 4.5): string {
  const toward = relativeLuminance(fg) > 0.5 ? BLACK : WHITE;
  let out = normalizeHex(color)!;
  for (let i = 1; i <= 40 && contrastRatio(fg, out) < min; i++) out = mix(color, toward, i / 40);
  return out;
}
